import { BadgePercent } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const OLD_FIELDS = [
  ["old_price_monthly", "Miesięcznie / mc"],
  ["old_price_3", "3 mc / mc"],
  ["old_price_6", "6 mc / mc"],
  ["old_price_annual", "12 mc / mc"],
  ["old_price_24", "24 mc / mc"],
  ["old_total_3", "3 mc łącznie"],
  ["old_total_6", "6 mc łącznie"],
  ["old_total_12", "12 mc łącznie"],
  ["old_total_24", "24 mc łącznie"],
];

const EMPTY = { enabled: false, label: "", description: "" };

export function PackagePromoEditor({ pkg, onChange }) {
  const promo = { ...EMPTY, ...(pkg.promo || {}) };
  const set = (k, v) => onChange({ ...promo, [k]: v });
  return (
    <div data-testid={`promo-editor-${pkg.id}`} className={`rounded-xl border p-4 space-y-4 ${promo.enabled ? "border-brand-red/50 bg-brand-red/5" : "border-dashed border-border"}`}>
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm font-semibold inline-flex items-center gap-2"><BadgePercent size={15} className="text-brand-red" /> Promocja dla pakietu {pkg.name}</p>
        <label className="inline-flex items-center gap-2 text-xs font-semibold">
          <Switch data-testid={`promo-enabled-${pkg.id}`} checked={promo.enabled} onCheckedChange={(v) => set("enabled", v)} />
          {promo.enabled ? "Włączona" : "Wyłączona"}
        </label>
      </div>
      {promo.enabled && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Etykieta na karcie (np. „3 miesiące gratis”, „-20%”)</label>
              <Input data-testid={`promo-label-${pkg.id}`} value={promo.label} maxLength={40} onChange={(e) => set("label", e.target.value)} placeholder="Promocja" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Opis promocji (pod ceną)</label>
              <Textarea data-testid={`promo-description-${pkg.id}`} rows={2} value={promo.description} maxLength={200} onChange={(e) => set("description", e.target.value)} placeholder="np. Przy płatności za 12 miesięcy dostajesz 3 miesiące gratis — oferta do 30.09." />
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-2">Ceny regularne (przekreślone). Aktualne ceny pakietu powyżej są cenami promocyjnymi — one trafiają do umowy i płatności. Zostaw puste, jeśli w danym okresie nie ma obniżki.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {OLD_FIELDS.map(([k, label]) => (
                <div key={k}>
                  <label className="text-[11px] font-semibold text-muted-foreground mb-1 block">{label}</label>
                  <Input data-testid={`promo-${k}-${pkg.id}`} value={promo[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder="—" />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function Billing24Editor({ content, onChange }) {
  return (
    <div data-testid="billing-24-editor" className="bg-card border border-border rounded-2xl p-6 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="font-display font-bold text-lg">Okres 24 miesięcy w cenniku</h3>
          <p className="text-xs text-muted-foreground mt-1">Dodaje zakładkę „24 miesiące z góry” w cenniku i w kreatorze zamówienia (umowa, proforma, płatność PayU). Ceny 24 mc wpisz przy każdym pakiecie powyżej — pakiet bez ceny 24 mc nie będzie miał tej opcji.</p>
        </div>
        <label className="inline-flex items-center gap-2 text-xs font-semibold shrink-0">
          <Switch data-testid="billing-24-enabled" checked={!!content.billing_24_enabled} onCheckedChange={(v) => onChange({ ...content, billing_24_enabled: v })} />
          {content.billing_24_enabled ? "Włączony" : "Wyłączony"}
        </label>
      </div>
      <div>
        <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Opis pokazywany po wybraniu zakładki 24 miesiące</label>
        <Textarea data-testid="billing-24-note-input" rows={3} value={content.billing_24_note || ""} maxLength={600} onChange={(e) => onChange({ ...content, billing_24_note: e.target.value })} placeholder="np. Płacąc za 24 miesiące z góry, gwarantujesz sobie stałą cenę przez 2 lata i oszczędzasz ... zł względem płatności rocznej." />
      </div>
    </div>
  );
}

export function PricingPromoEditor({ value, onChange }) {
  const promo = { enabled: false, title: "", text: "", ...(value || {}) };
  const set = (k, v) => onChange({ ...promo, [k]: v });
  return (
    <div data-testid="pricing-promo-editor" className="bg-card border border-border rounded-2xl p-6 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="font-display font-bold text-lg inline-flex items-center gap-2"><BadgePercent size={18} className="text-brand-red" /> Baner promocji w cenniku</h3>
          <p className="text-xs text-muted-foreground mt-1">Czerwony pasek nad pakietami — informacja o aktualnej akcji promocyjnej (np. „Jesienna promocja: 3 miesiące gratis”).</p>
        </div>
        <label className="inline-flex items-center gap-2 text-xs font-semibold shrink-0">
          <Switch data-testid="pricing-promo-enabled" checked={promo.enabled} onCheckedChange={(v) => set("enabled", v)} />
          {promo.enabled ? "Widoczny" : "Ukryty"}
        </label>
      </div>
      <div>
        <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Tytuł</label>
        <Input data-testid="pricing-promo-title-input" value={promo.title} maxLength={120} onChange={(e) => set("title", e.target.value)} placeholder="np. Promocja jesienna — 3 miesiące gratis" />
      </div>
      <div>
        <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Opis</label>
        <Textarea data-testid="pricing-promo-text-input" rows={3} value={promo.text} maxLength={600} onChange={(e) => set("text", e.target.value)} placeholder="Szczegóły promocji, warunki, termin obowiązywania." />
      </div>
    </div>
  );
}
